import cookies from 'js-cookie';
import { isInteger, merge, omitBy } from 'lodash';
import { oidc, windowWrapper } from '@clubmed/utils';
import { getFromLocalStorage } from 'Client/local-storage-api';
import { getTimestamp, isYearsRangeValid } from 'Utils/date/dateUtils';
import {
  DEPARTURE_DATE_MAX_DATE,
  KIDS_NUMBER_MAX_SELLER,
  MAX_PAX,
  MAX_PAX_SELLER,
  QUERY_QUERY_PARAM,
  SORT_BY_PRICE_QUERY_PARAM,
} from 'Utils/faceting/constants';
import {
  customFacetsQueryParamMapping,
  facetsQueryParamMapping,
  getActiveFacetsFromQueryParams,
  unserializeKidsFromQueryParam,
} from 'Utils/faceting/facetingUtils';
import { getValidationStateFromKids } from 'Utils/faceting/kidsValidationStateUtils';
import { SELLER_INFO, USER_INFO } from 'Utils/ple-utils/cookies';
import { getCxoTravelers, getNeolids } from 'Utils/seller/sellerUtils';
import {
  QUERY_CMTA,
  QUERY_CXO_SALES_NETWORK_ID,
  QUERY_DATE_FORMAT,
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_NUMBER_OF_ADULTS,
  QUERY_TRANSPORT,
} from 'Utils/uri/constants';
import { getQueryParameters } from 'Utils/uri/uriUtils';
import { getDefaultSelectedMonth } from './calendar/calendar.helper';
import { DEFAULT_ADULTS_NB, DEFAULT_ADULT_VALUE } from './trip-information/adults/constants';
import { DEFAULT_KID_VALUE } from './trip-information/kids/constants';
export const LOCAL_STORAGE_STATE_KEY = 'state';

/**
 * Handle legacy state saved as a string
 * Remove completly after some time in production (~6 months ?)
 */
function parse(state) {
  if (typeof state === 'string') {
    return JSON.parse(state);
  }

  return state;
}

function stateFromLocalStorage(locale: string): ReduxState {
  const serializedState = getFromLocalStorage(`${LOCAL_STORAGE_STATE_KEY}-${locale}`);

  if (!serializedState) {
    return {};
  }

  const {
    seller,
    tripInformation: { kids },
  } = merge(parse(serializedState), {
    tripInformation: {
      kids: [],
    },
  });
  return {
    faceting: {
      kidsValidationState: getValidationStateFromKids(kids),
    },
    tripInformation: {
      kids: kids.map(({ birthdate }) => ({ ...DEFAULT_KID_VALUE, birthdate })),
      adults: kids.length > 0 ? Array(DEFAULT_ADULTS_NB).fill(DEFAULT_ADULT_VALUE) : [],
    },
    seller,
  };
}

type RequestCookies = Record<string, string | undefined>;

function getCookieValue(cookieName: string, serverCookies?: RequestCookies): string | undefined {
  if (serverCookies) {
    const value = serverCookies[cookieName];
    return typeof value === 'string' ? value : undefined;
  }

  return cookies.get(cookieName);
}

export function stateFromCookies(serverCookies?: RequestCookies): ReduxState {
  const encodedUserInfo = getCookieValue(USER_INFO, serverCookies);
  const userInfo = encodedUserInfo ? oidc.fromUriEncodedState(encodedUserInfo) : {};
  const encodedSellerInfo = getCookieValue(SELLER_INFO, serverCookies);
  const seller = encodedSellerInfo
    ? {
        seller: {
          sellerInfo: oidc.fromUriEncodedState(encodedSellerInfo),
        },
      }
    : {};
  return {
    userInfo,
    ...seller,
  };
}

function parseCxoKids(cxoKids) {
  return cxoKids.length <= KIDS_NUMBER_MAX_SELLER
    ? cxoKids.map(({ neolid, birthdate }) => ({
        neolid,
        birthdate,
      }))
    : [];
}

function parseCxoAdults(cxoAdults) {
  return cxoAdults.length <= MAX_PAX_SELLER
    ? cxoAdults.map(({ neolid, birthdate }) => ({ ...DEFAULT_ADULT_VALUE, neolid, birthdate }))
    : [{}, {}];
}

function parseAdultsQueryParam(adultQueryParam: string) {
  const nbAdults = parseInt(adultQueryParam, 10);
  const hasAdults = isInteger(nbAdults) && nbAdults <= MAX_PAX_SELLER;
  return hasAdults ? Array(nbAdults).fill(DEFAULT_ADULT_VALUE) : [];
}

function stateFromQueryParams(): ReduxState {
  // NOTE: getCxoTravelers() and getNeolids() rely on queryparams
  const neolids = getNeolids();
  const queryParams = getQueryParameters(windowWrapper.getLocation()) || {};
  const {
    [QUERY_DEPARTURE_DATE]: departureDate,
    [QUERY_CMTA]: cmtaQueryParam,
    [QUERY_CXO_SALES_NETWORK_ID]: cxoQueryParam,
  } = queryParams;
  const isSeller = typeof cmtaQueryParam !== 'undefined' || Boolean(cxoQueryParam);
  const departureDateIsValid = isYearsRangeValid(
    queryParams[QUERY_DEPARTURE_DATE],
    QUERY_DATE_FORMAT,
    DEPARTURE_DATE_MAX_DATE,
  );
  const kids = unserializeKidsFromQueryParam(queryParams[QUERY_KIDS], isSeller);
  const adults = parseAdultsQueryParam(queryParams[QUERY_NUMBER_OF_ADULTS]);
  const numberAttendees = kids.length + adults.length;
  const maxPaxByUser = isSeller ? MAX_PAX_SELLER : MAX_PAX;
  const numberAttendeesNotAllowed = numberAttendees > maxPaxByUser;
  return {
    calendar: {
      selectedMonth: departureDateIsValid ? getDefaultSelectedMonth(departureDate) : undefined,
    },
    // null states are explicitly omitted further
    faceting: {
      activeCustomFacets: getActiveFacetsFromQueryParams(
        queryParams,
        customFacetsQueryParamMapping,
      ),
      activeFacets: getActiveFacetsFromQueryParams(queryParams, facetsQueryParamMapping),
      query: {
        label: queryParams[QUERY_QUERY_PARAM] || '',
      },
      kidsValidationState: getValidationStateFromKids(kids),
      orderByPrice: ['asc', 'desc'].includes(queryParams[SORT_BY_PRICE_QUERY_PARAM])
        ? queryParams[SORT_BY_PRICE_QUERY_PARAM]
        : '',
    },
    facetingTripInformation: neolids.length > 0 ? null : undefined,
    seller:
      neolids.length > 0
        ? {
            neolids,
          }
        : undefined,
    tripCriteria: {
      departureDate: departureDateIsValid
        ? getTimestamp(departureDate, QUERY_DATE_FORMAT)
        : undefined,
      duration: parseInt(queryParams[QUERY_DURATION], 10) || null,
      departureCity: queryParams[QUERY_TRANSPORT],
    },
    tripInformation: {
      kids: numberAttendeesNotAllowed ? [] : kids,
      adults:
        (adults.length === 0 && kids.length) || numberAttendeesNotAllowed
          ? Array(DEFAULT_ADULTS_NB).fill(DEFAULT_ADULT_VALUE)
          : adults,
    },
  };
}

function getSellerTripInformation() {
  // CMTA sellers
  const queryParams = getQueryParameters(windowWrapper.getLocation()) || {};
  const { [QUERY_CMTA]: cmtaQueryParam } = queryParams;

  if (typeof cmtaQueryParam !== 'undefined') {
    const { tripInformation } = stateFromQueryParams();
    return tripInformation;
  }

  // CXO sellers
  const cxoTravelers = getCxoTravelers() || [];

  if (cxoTravelers.length === 0) {
    return;
  }

  const numberAttendeesAllowed = cxoTravelers.length > 0 && cxoTravelers.length <= MAX_PAX_SELLER;
  return {
    kids: numberAttendeesAllowed ? parseCxoKids(cxoTravelers.filter(({ isChild }) => isChild)) : [],
    adults: numberAttendeesAllowed
      ? parseCxoAdults(cxoTravelers.filter(({ isChild }) => !isChild))
      : Array(DEFAULT_ADULTS_NB).fill(DEFAULT_ADULT_VALUE),
  };
}

function cleanTripInfoKids(state: ReduxState): ReduxState {
  if (!state.tripInformation) {
    return state;
  }

  const { kids = [] } = state.tripInformation as { kids?: Array<Record<string, unknown>> };
  const cleanedKids = kids.map(({ neolid, birthdate, childcareIncluded = false }) => ({
    neolid,
    birthdate,
    childcareIncluded,
  }));
  return {
    ...state,
    tripInformation: { ...(state.tripInformation as Record<string, unknown>), kids: cleanedKids },
  };
}

export function getInitialReduxState(initialState: ReduxState, locale: string): ReduxState {
  let state = { ...initialState, ...stateFromLocalStorage(locale), ...stateFromCookies() };
  // NOTE: With merge() behavior, subvalues that are undefined won't override existing values
  // ie: merge({ a: 1, b: 2 }, { a: undefined, b: null }) returns { a: 1, b: null }
  state = merge({}, state, stateFromQueryParams());
  // for seller erase tripInformation
  state = {
    ...state,
    tripInformation: {
      ...(state.tripInformation as Record<string, unknown>),
      ...(getSellerTripInformation() as Record<string, unknown>),
    },
  };
  state = cleanTripInfoKids(state);
  state = omitBy(state, (value) => value === null);
  return state;
}
